import React, { useEffect, useRef, useState } from "react";
import "./tailwindstyles.css";
import {
  FaBolt,
  FaMicrochip,
  FaChartLine,
  FaBell,
} from "react-icons/fa";

const Info = () => {
  const sectionRef = useRef(null);
  const [hoveredBox, setHoveredBox] = useState(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const boxes = entry.target.querySelectorAll(".animate-box");

          boxes.forEach((box, index) => {
            setTimeout(() => {
              box.classList.add("box-visible");
            }, index * 120);
          });

          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersect,
      observerOptions
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const boxData = [
    {
      title: "Smart Energy Monitoring",
      points: [
        "Monitor power usage across campuses & industries.",
        "Track transformers, feeders & loads instantly.",
        "Reduce operational costs using live insights.",
      ],
      accentGradient: "from-yellow-400 to-orange-500",
      iconGradient: "from-yellow-300 to-orange-500",
      cardBg:
        "from-[#1a1408]/90 via-[#22180b]/80 to-[#0f172a]/90",
      borderColor: "border-yellow-500/20",
      icon: <FaBolt size={20} />,
      number: "01",
      glow: "shadow-yellow-500/20",
    },

    {
      title: "IoT Device Integration",
      points: [
        "Integrate smart meters & IoT sensors seamlessly.",
        "Automated real-time data acquisition.",
        "Secure enterprise-grade infrastructure.",
      ],
      accentGradient: "from-sky-400 to-blue-500",
      iconGradient: "from-sky-300 to-blue-500",
      cardBg:
        "from-[#081722]/90 via-[#0d1f2e]/80 to-[#0f172a]/90",
      borderColor: "border-sky-500/20",
      icon: <FaMicrochip size={20} />,
      number: "02",
      glow: "shadow-sky-500/20",
    },

    {
      title: "Real-Time Analytics",
      points: [
        "Analyze voltage, current & load trends instantly.",
        "Detect abnormal usage & power imbalance.",
        "AI-powered predictive analytics engine.",
      ],
      accentGradient: "from-green-400 to-emerald-500",
      iconGradient: "from-green-300 to-emerald-500",
      cardBg:
        "from-[#081b16]/90 via-[#0d241d]/80 to-[#0f172a]/90",
      borderColor: "border-green-500/20",
      icon: <FaChartLine size={20} />,
      number: "03",
      glow: "shadow-green-500/20",
    },

    {
      title: "Fault Alert System",
      points: [
        "Receive overload & abnormal usage alerts.",
        "Email, SMS & dashboard notifications.",
        "Improve reliability with smart detection.",
      ],
      accentGradient: "from-red-400 to-orange-500",
      iconGradient: "from-red-300 to-orange-500",
      cardBg:
        "from-[#22110d]/90 via-[#2b1610]/80 to-[#0f172a]/90",
      borderColor: "border-red-500/20",
      icon: <FaBell size={20} />,
      number: "04",
      glow: "shadow-red-500/20",
    },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden text-slate-200 py-16">

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>

      {/* Animated Glow */}
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div
          className="absolute top-0 left-0 w-[120%] h-[120%]
          bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
          blur-3xl animate-slowWave"
        ></div>

        <div
          className="absolute bottom-0 right-0 w-[120%] h-[120%]
          bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
          blur-3xl animate-fastWave"
        ></div>
      </div>

      {/* Grid Overlay */}
      <div
        className="absolute inset-0 pointer-events-none
        bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      ></div>

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-[1650px] mx-auto px-8">

        {/* Header */}
        <div className="text-center mb-14">

          <div className="inline-flex items-center px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl mb-5">
            <span className="text-[11px] tracking-[4px] uppercase text-slate-300">
              Power Sense Platform
            </span>
          </div>

          <h2
            className="text-4xl md:text-6xl font-black mb-5
            bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300
            text-transparent bg-clip-text
            animate-gradientMove bg-[length:200%_200%]"
          >
            Intelligent Energy Ecosystem
          </h2>

          <p className="text-base md:text-lg text-slate-300 max-w-4xl mx-auto leading-relaxed">
            Advanced IoT-driven monitoring, analytics and automation platform
            for modern energy infrastructure & smart power management systems.
          </p>
        </div>

        {/* Cards */}
        <div
          ref={sectionRef}
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7 items-stretch"
        >
          {boxData.map((box, index) => (
            <div
              key={index}
              className="animate-box opacity-0 translate-y-14 scale-[0.96]
              transition-all duration-1000 ease-out"
              onMouseEnter={() => setHoveredBox(index)}
              onMouseLeave={() => setHoveredBox(null)}
            >

              {/* Card */}
              <div
                className={`group relative h-full min-h-[390px]
                bg-gradient-to-br ${box.cardBg} backdrop-blur-2xl border ${box.borderColor}
                rounded-3xl p-7 overflow-hidden
                transition-all duration-500
                hover:-translate-y-4 hover:scale-[1.02]
                hover:shadow-[0_20px_80px_rgba(0,0,0,0.45)]
                ${box.glow}`}
              >

                {/* Hover Glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-25
                  transition-all duration-700
                  bg-gradient-to-r ${box.accentGradient}
                  blur-3xl`}
                ></div>

                {/* Floating Background Number */}
                <div
                  className="absolute right-4 top-0 text-[130px]
                  font-black text-white/[0.04]
                  leading-none select-none"
                >
                  {box.number}
                </div>

                {/* Animated Border */}
                <div
                  className={`absolute top-0 left-0 h-[3px]
                  bg-gradient-to-r ${box.accentGradient}
                  transition-all duration-700
                  ${hoveredBox === index ? "w-full" : "w-0"}`}
                ></div>

                {/* Icon */}
                <div
                  className={`relative z-10 w-16 h-16 rounded-2xl
                  flex items-center justify-center
                  bg-gradient-to-br ${box.iconGradient}
                  text-white shadow-2xl mb-6
                  transition-all duration-500
                  group-hover:scale-110 group-hover:rotate-6`}
                >
                  {box.icon}
                </div>

                {/* Title */}
                <h3
                  className="relative z-10 text-2xl font-bold
                  mb-4 text-white leading-snug"
                >
                  {box.title}
                </h3>

                {/* Underline */}
                <div
                  className={`relative z-10 h-1 w-16
                  bg-gradient-to-r ${box.accentGradient}
                  rounded-full mb-6`}
                ></div>

                {/* Points */}
                <ul
                  className="relative z-10 space-y-4
                  text-[15px] text-slate-300 leading-relaxed"
                >
                  {box.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">

                      <div
                        className={`w-2 h-2 rounded-full mt-2.5 mr-3
                        bg-gradient-to-r ${box.accentGradient}`}
                      ></div>

                      <span>{point}</span>

                    </li>
                  ))}
                </ul>

                {/* Bottom Line */}
                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/5">
                  <div
                    className={`h-full bg-gradient-to-r ${box.accentGradient}
                    transition-all duration-700
                    ${hoveredBox === index ? "w-full" : "w-0"}`}
                  ></div>
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Animations */}
      <style>{`

        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
          50% {
            transform: translate(10%, 10%) rotate(14deg);
          }
          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }
          50% {
            transform: translate(-10%, -10%) rotate(-14deg);
          }
          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes floating {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
          100% {
            transform: translateY(0px);
          }
        }

        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradientMove {
          animation: gradientMove 6s ease infinite;
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

        .box-visible {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        .animate-box {
          animation: floating 6s ease-in-out infinite;
        }

      `}</style>
    </section>
  );
};

export default Info;